import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/api/client";
import { useAuth } from "@/auth/AuthContext";
import type { ProxyKey, RealKey } from "@/types";

type Item = {
  id: string;
  label: string;
  sublabel?: string;
  group: "Navigation" | "Real keys" | "Proxy keys" | "Actions";
  onRun: () => void;
};

function matches(needle: string, hay: string): boolean {
  const n = needle.toLowerCase();
  const h = hay.toLowerCase();
  // Subsequence match — "antp" matches "anthropic-prod"
  let i = 0;
  for (const c of h) {
    if (c === n[i]) i++;
    if (i >= n.length) return true;
  }
  return n === "";
}

export function CommandPalette() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [cursor, setCursor] = useState(0);
  const [reals, setReals] = useState<RealKey[]>([]);
  const [proxies, setProxies] = useState<ProxyKey[]>([]);
  const nav = useNavigate();
  const auth = useAuth();
  const inputRef = useRef<HTMLInputElement>(null);

  // Toggle with Cmd+K / Ctrl+K. Also support `?` for shortcuts-help and `g ...` chords
  // on the top-level so they don't require the palette to be open first.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((v) => !v);
        return;
      }
      if (open && e.key === "Escape") {
        setOpen(false);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  // Lazy-load keys the first time the palette opens (and refresh when it reopens).
  useEffect(() => {
    if (!open || !auth.me) return;
    setQuery("");
    setCursor(0);
    Promise.all([
      api<RealKey[]>("/api/admin/real-keys").catch(() => []),
      api<ProxyKey[]>("/api/admin/proxy-keys").catch(() => []),
    ]).then(([r, p]) => {
      setReals(r);
      setProxies(p);
    });
    setTimeout(() => inputRef.current?.focus(), 0);
  }, [open, auth.me]);

  const items = useMemo<Item[]>(() => {
    const nav_items: Item[] = [
      { id: "nav:dash", group: "Navigation", label: "Go to Dashboard", sublabel: "g d", onRun: () => nav("/") },
      { id: "nav:real", group: "Navigation", label: "Go to Real keys", sublabel: "g r", onRun: () => nav("/real-keys") },
      { id: "nav:proxy", group: "Navigation", label: "Go to Proxy keys", sublabel: "g p", onRun: () => nav("/proxy-keys") },
      { id: "nav:keys", group: "Navigation", label: "Go to Keys tree", sublabel: "g k", onRun: () => nav("/keys") },
      { id: "nav:import", group: "Navigation", label: "Import from .env paste", sublabel: "g i", onRun: () => nav("/import") },
      { id: "nav:audit", group: "Navigation", label: "Go to Audit log", sublabel: "g a", onRun: () => nav("/audit") },
      { id: "nav:approvals", group: "Navigation", label: "Go to Approvals (4-eyes)", sublabel: "g v", onRun: () => nav("/approvals") },
      { id: "nav:users", group: "Navigation", label: "Go to Users", sublabel: "g u", onRun: () => nav("/users") },
      { id: "nav:settings", group: "Navigation", label: "Go to Settings", sublabel: "g s", onRun: () => nav("/settings") },
    ];
    const actions: Item[] = [
      {
        id: "act:new-real",
        group: "Actions",
        label: "Add real key",
        sublabel: "new upstream credential",
        onRun: () => nav("/real-keys?new=1"),
      },
      {
        id: "act:new-proxy",
        group: "Actions",
        label: "Issue proxy key",
        sublabel: "new vlt_live_… token",
        onRun: () => nav("/proxy-keys?new=1"),
      },
      {
        id: "act:logout",
        group: "Actions",
        label: "Sign out",
        onRun: async () => {
          await auth.logout();
          nav("/login");
        },
      },
    ];
    const real_items: Item[] = reals.map((r) => ({
      id: `r:${r.id}`,
      group: "Real keys",
      label: r.label,
      sublabel: `${r.provider} · ${r.upstream_base_url}`,
      onRun: () => nav(`/real-keys/${r.id}`),
    }));
    const proxy_items: Item[] = proxies.map((p) => ({
      id: `p:${p.id}`,
      group: "Proxy keys",
      label: p.label,
      sublabel: `${p.prefix}… · project=${p.project ?? "—"}${p.revoked_at ? " · revoked" : ""}`,
      onRun: () => nav(`/proxy-keys/${p.id}`),
    }));
    return [...nav_items, ...actions, ...real_items, ...proxy_items];
  }, [reals, proxies, nav, auth]);

  const filtered = useMemo(() => {
    if (!query) return items;
    return items.filter(
      (it) => matches(query, it.label) || (it.sublabel && matches(query, it.sublabel))
    );
  }, [items, query]);

  useEffect(() => {
    if (cursor >= filtered.length) setCursor(0);
  }, [filtered, cursor]);

  function runAt(idx: number) {
    const it = filtered[idx];
    if (!it) return;
    setOpen(false);
    setTimeout(() => it.onRun(), 0);
  }

  // Group items for rendering — preserve source order within groups.
  const grouped = useMemo(() => {
    const out: { group: Item["group"]; items: Item[] }[] = [];
    for (const it of filtered) {
      const last = out[out.length - 1];
      if (last && last.group === it.group) last.items.push(it);
      else out.push({ group: it.group, items: [it] });
    }
    return out;
  }, [filtered]);

  if (!open) return null;

  // Re-index cursor across the flat filtered list; groups are purely display-level.
  let flatIdx = -1;
  return (
    <div
      className="fixed inset-0 z-[60] grid place-items-start justify-center pt-[14vh] bg-black/60 backdrop-blur-sm"
      onClick={() => setOpen(false)}
    >
      <div
        className="w-full max-w-xl rounded-2xl border border-white/10 bg-ink-800/95 shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <input
          ref={inputRef}
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setCursor(0);
          }}
          onKeyDown={(e) => {
            if (e.key === "ArrowDown") {
              e.preventDefault();
              setCursor((c) => Math.min(c + 1, filtered.length - 1));
            } else if (e.key === "ArrowUp") {
              e.preventDefault();
              setCursor((c) => Math.max(c - 1, 0));
            } else if (e.key === "Enter") {
              e.preventDefault();
              runAt(cursor);
            }
          }}
          placeholder="Search keys, jump anywhere…"
          className="w-full bg-transparent px-5 py-4 border-b border-white/10 text-slate-100 placeholder:text-slate-500 outline-none"
          autoFocus
        />
        <div className="max-h-[55vh] overflow-y-auto py-2">
          {grouped.length === 0 && (
            <div className="px-5 py-6 text-sm text-slate-500">No matches.</div>
          )}
          {grouped.map((g) => (
            <div key={g.group}>
              <div className="px-5 pt-3 pb-1 text-[10px] uppercase tracking-wider text-slate-500">
                {g.group}
              </div>
              {g.items.map((it) => {
                flatIdx += 1;
                const active = flatIdx === cursor;
                const myIdx = flatIdx;
                return (
                  <button
                    key={it.id}
                    onMouseEnter={() => setCursor(myIdx)}
                    onClick={() => runAt(myIdx)}
                    className={`w-full text-left flex items-center justify-between gap-3 px-5 py-2 text-sm transition-colors ${
                      active ? "bg-accent-500/15 text-slate-50" : "text-slate-300 hover:bg-white/5"
                    }`}
                  >
                    <div className="truncate">
                      <div className="truncate font-medium">{it.label}</div>
                      {it.sublabel && (
                        <div className="truncate text-xs text-slate-500 mono">{it.sublabel}</div>
                      )}
                    </div>
                    {active && <span className="text-[10px] text-accent-500">↵</span>}
                  </button>
                );
              })}
            </div>
          ))}
        </div>
        <div className="flex items-center justify-between px-5 py-2 border-t border-white/10 text-[11px] text-slate-500">
          <span>
            <kbd className="mono px-1.5 py-0.5 rounded bg-white/5 border border-white/10">↑↓</kbd> navigate
            {"  "}
            <kbd className="mono px-1.5 py-0.5 rounded bg-white/5 border border-white/10">↵</kbd> open
            {"  "}
            <kbd className="mono px-1.5 py-0.5 rounded bg-white/5 border border-white/10">esc</kbd> close
          </span>
          <span>⌘K to toggle</span>
        </div>
      </div>
    </div>
  );
}
